/* eslint-disable @typescript-eslint/no-explicit-any */
import { default as Input } from 'antd/es/input';
import { default as Select } from 'antd/es/select';
import type { ComponentProps } from 'react';
import { useMemo } from 'react';

import { TooltipIconWrapper } from '@atoms/TooltipIconWrapper';

import { AmountInput } from '@molecules/AmountInput';
import { BusinessUnitSelector } from '@molecules/BusinessUnitSelector';
import { IBANInput } from '@molecules/IBANInput';
import { PurposeCodeSelector } from '@molecules/PurposeCodeSelector';
import { SelectCountry } from '@molecules/SelectCountry';
import { SelectVirtualAccount } from '@molecules/SelectVirtualAccount';

import type { FieldDefinition } from './utils/fieldDefinitionEnhancer';

import { SeparatedDateField } from '../CreateManualPayoutDialog/fields/SeparatedDateField';

type FieldFactoryProps = {
  onChange: (...event: any[]) => void;
  value: any;
  field: FieldDefinition;
  className?: ComponentProps<'div'>['className'];
};

export const FieldFactory = (props: FieldFactoryProps) => {
  const children = useMemo(() => {
    return fieldElementFactory(props);
  }, [props]);

  return (
    <div className="flex w-full flex-col gap-1">
      <span className="w-fit text-sm">
        {props.field.tooltip ? (
          <TooltipIconWrapper tooltipTitle={props.field.tooltip}>
            {props.field.label}
          </TooltipIconWrapper>
        ) : (
          props.field.label
        )}
      </span>
      {children}
      {props.field.example && (
        <span className="text-xs text-gray-600">{props.field.example}</span>
      )}
    </div>
  );
};

const fieldElementFactory = ({ field, ...restProps }: FieldFactoryProps) => {
  const props = {
    placeholder: field.placeholder,
    'data-testid': field.fullName,
    ...restProps
  };

  switch (field.kind) {
    case 'text':
      return <Input {...props} />;

    case 'amount':
      return <AmountInput {...props} />;

    case 'virtual_account':
      return (
        <SelectVirtualAccount
          onChangeCallback={props.onChange}
          value={props.value}
          className={props.className}
          allowAll={false}
          e2eId={field.fullName}
          filterTerminatedAccounts={true}
          filterSuspendedAccounts={true}
          filterCryptoAccounts={true}
        />
      );

    case 'iban':
      return <IBANInput {...props} />;

    case 'country':
      return <SelectCountry {...props} />;

    case 'date':
      return <SeparatedDateField {...props} />;

    case 'b2b_purpose_code':
      return <PurposeCodeSelector {...props} />;

    case 'business_unit':
      return <BusinessUnitSelector {...props} />;

    case 'creditor_type':
      return (
        <Select
          options={[
            { value: 'private', label: 'Private' },
            { value: 'organization', label: 'Organization' }
          ]}
          {...props}
        />
      );

    case 'e2e_test_case':
      return (
        <Select
          options={[
            { value: 'HOLD', label: 'Hold' },
            { value: 'RETURNED', label: 'Returned' }
          ]}
          {...props}
        />
      );

    default:
      return <Input {...props} />;
  }
};
